import { cn } from "@/lib/utils";
import { CheckSquare } from "lucide-react";
import { Badge } from "../../ui/badge";

import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export type ActionItemStatus =
  | "ready"
  | "in_progress"
  | "completed"
  | "failed"
  | "awaiting_input";

export type ActionItem = {
  id: string;
  title: string;
  description: string;
  status?: ActionItemStatus;
  created_at?: string;
  updated_at?: string;
};

export type ActionItemCardProps = ActionItem & BaseEntityCardProps;

export function ActionItemCardAvatar({
  className,
  style,
}: Partial<ActionItemCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <CheckSquare className="h-5 w-5 text-muted-foreground" style={style} />
    </BaseEntityCardIcon>
  );
}

const getStatusConfig = (status?: ActionItemStatus) => {
  switch (status) {
    case "in_progress":
      return {
        label: "In Progress",
        color: "text-teal dark:text-teal border-rust/20",
      };
    case "completed":
      return {
        label: "Done",
        color: "text-lc-active dark:text-lc-active border-green-600/20",
      };
    case "failed":
      return {
        label: "Failed",
        color: "text-lc-blocked dark:text-lc-blocked border-red-600/20",
      };
    case "awaiting_input":
      return {
        label: "Awaiting Input",
        color: "text-lc-warning dark:text-lc-warning border-amber-600/20",
      };
    default:
      return {
        label: "Ready",
        color: "text-muted-foreground",
      };
  }
};

export function ActionItemCard({
  title = "",
  description = "",
  status,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: ActionItemCardProps) {
  const statusConfig = getStatusConfig(status);

  const descriptionPreview =
    description && description.length > 100
      ? `${description.toString().substring(0, 100)}...`
      : description.toString();

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
      align="start"
    >
      <ActionItemCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            {statusConfig && (
              <Badge
                variant="outline"
                className={cn("text-xs", statusConfig.color)}
              >
                {statusConfig.label}
              </Badge>
            )}
          </BaseEntityCardTags>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
